import { bogoLabel } from "../utils/helpers.js";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { BloomMark } from "./Icon.jsx";
const Rupee = () => <span style={{ fontFamily: 'system-ui, -apple-system, "Segoe UI", Roboto, Arial, sans-serif' }}>₹</span>;

const BACKEND_URL = import.meta.env.VITE_BACKEND_URL || "http://localhost:3001";

function RecommendationRow({ title, products }) {
  const navigate = useNavigate();
  
  if (!products || products.length === 0) {
    return null;
  }

  return (
    <div style={{marginBottom:"40px"}}>
      <h3 style={{fontSize:"16px", fontWeight:700, color:"var(--ink)", marginBottom:"16px"}}>
        {title}
      </h3>
      <div style={{display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(140px, 1fr))", gap:"16px"}}>
        {products.slice(0, 8).map(product => (
          <div
            key={product.id}
            onClick={() => navigate(`/product/${product.id}`)}
            style={{cursor:"pointer", borderRadius:"4px", overflow:"hidden", transition:"transform 0.2s", border:"1px solid var(--border)"}}
            onMouseEnter={e => e.currentTarget.style.transform = "translateY(-4px)"}
            onMouseLeave={e => e.currentTarget.style.transform = "none"}
          >
            {/* Image */}
            <div style={{width:"100%", paddingBottom:"100%", position:"relative", background:"var(--surface)", overflow:"hidden"}}>
              <img
                src={product.img}
                alt={product.name}
                style={{position:"absolute", top:0, left:0, width:"100%", height:"100%", objectFit:"cover"}}
              />
              {/* Offer ribbon — renders only when we know the store's offer */}
              {product.bogo_eligible && bogoLabel(product._storeInfo) && (
                <div style={{position:"absolute", top:6, right:6, zIndex:2, background:"var(--warn-bg)", color:"var(--warn-fg)", border:"1px solid var(--warn-bd)", fontSize:8, fontWeight:800, letterSpacing:"0.06em", padding:"3px 6px", borderRadius:4, textTransform:"uppercase", lineHeight:1.15, maxWidth:"80%", textAlign:"center", pointerEvents:"none"}}>
                  {bogoLabel(product._storeInfo)}
                </div>
              )}
            </div>
            
            {/* Info */}
            <div style={{padding:"12px"}}>
              <div style={{fontSize:"12px", fontWeight:600, color:"var(--ink)", lineHeight:"1.3", marginBottom:"6px", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap"}}>
                {product.name}
              </div>
              <div style={{display:"flex", alignItems:"center", gap:"6px"}}>
                <span style={{fontSize:"13px", fontWeight:700, color:"var(--ink)"}}>
                  <Rupee/>{product.price}
                </span>
                {product.mrp && (
                  <span style={{fontSize:"11px", color:"var(--ink-soft)", textDecoration:"line-through"}}>
                    <Rupee/>{product.mrp}
                  </span>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// storeOnly (default false): render ONLY the "More from this store" row and skip the
// cross-store popular/similar fetches. Used by the storefront quick-view modal so a store's
// own product detail keeps the shopper inside that store instead of surfacing rival stores.
export function ProductRecommendations({ currentProductId, currentStoreId, currentCategory, storeOnly = false }) {
  const [sameStoreProducts, setSameStoreProducts] = useState([]);
  const [popularProducts, setPopularProducts] = useState([]);
  const [similarProducts, setSimilarProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchRecommendations = async () => {
      setLoading(true);
      try {
        // The endpoint returns { products, total } — normalise either shape to an array.
        const listOf = (data) => Array.isArray(data?.products) ? data.products : (Array.isArray(data) ? data : []);

        // 1. Same store products
        if (currentStoreId) {
          const res = await fetch(
            `${BACKEND_URL}/api/marketplace/products?store_id=${encodeURIComponent(currentStoreId)}&exclude=${encodeURIComponent(currentProductId)}&limit=8`
          );
          if (res.ok) {
            setSameStoreProducts(listOf(await res.json()));
          }
        }

        // Cross-store rows are skipped entirely in storeOnly mode.
        if (!storeOnly) {
          // 2. Popular products
          const res = await fetch(`${BACKEND_URL}/api/marketplace/sections?section=popular&limit=8`);
          if (res.ok) {
            const data = await res.json();
            const products = data.popular || [];
            setPopularProducts(products.filter(p => p.id !== currentProductId));
          }

          // 3. Similar products by category
          if (currentCategory) {
            const res2 = await fetch(
              `${BACKEND_URL}/api/marketplace/products?category=${encodeURIComponent(currentCategory)}&exclude=${encodeURIComponent(currentProductId)}&limit=8`
            );
            if (res2.ok) {
              setSimilarProducts(listOf(await res2.json()));
            }
          }
        }
      } catch (e) {
        console.error("Error fetching recommendations:", e);
      } finally {
        setLoading(false);
      }
    };

    fetchRecommendations();
  }, [currentProductId, currentStoreId, currentCategory, storeOnly]);

  if (loading) {
    return (
      <div style={{padding:"40px 20px", textAlign:"center", color:"var(--ink-soft)", fontSize:"14px"}}>
        Loading recommendations...
      </div>
    );
  }

  // storeOnly: single focused row; render nothing if the store has no other products.
  if (storeOnly) {
    if (sameStoreProducts.length === 0) return null;
    return (
      <div style={{paddingTop:"24px", borderTop:"1px solid var(--border)"}}>
        <RecommendationRow title="More from this store" products={sameStoreProducts}/>
      </div>
    );
  }

  return (
    <div style={{paddingTop:"40px", borderTop:"1px solid var(--border)"}}>
      <h2 style={{fontSize:"20px", fontWeight:800, color:"var(--ink)", marginBottom:"24px"}}>
        Recommended For You
      </h2>

      <RecommendationRow
        title="From the same store"
        products={sameStoreProducts}
      />

      <RecommendationRow
        title="Popular right now"
        products={popularProducts}
      />

      <RecommendationRow
        title="Similar products"
        products={similarProducts}
      />

      {sameStoreProducts.length === 0 && popularProducts.length === 0 && similarProducts.length === 0 && (
        <div style={{padding:"40px 20px", textAlign:"center", color:"var(--ink-soft)", fontSize:"14px"}}>
          No recommendations available yet. Check back soon! <BloomMark />
        </div>
      )}
    </div>
  );
}
